import React, { useEffect, useRef, useState } from 'react';
import CurrencyInput from 'react-currency-input-field';
import {
    faCircleCheck,
    faCreditCard,
    faDownload,
    faEye,
    faFileLines,
    faFilePdf,
    faUpload,
    faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    ApprovalState,
    FeeAgreementParty,
    LegalFees as LegalFeesModel,
    WireInstructions,
} from '../../../models/FlowOfFunds/flow-of-funds.model';
import {
    EM_DASH,
    formatDate,
    formatFileSize,
    formatFileType,
    formatMoneyExact,
} from '../../../utils/flowOfFundsFormat';
import ConfirmationModal from '../../Modal/ConfirmationModal';
import { NotificationFlag } from '../../NotificationFlag/NotificationFlag';
import { NotificationFlagsCategories } from '../../../models/NotificationFlags/NotificationFlagsCategories';
import { FeeAgreementBlock } from '../FeeAgreementBlock/FeeAgreementBlock';
import './LegalFees.scss';

const PDF_CONTENT_TYPE = 'application/pdf';

interface LegalFeesProps {
    engagementId: number;
    legalFees: LegalFeesModel;
    onAmountChange: (amount: number | null) => void;
    onInvoiceUpload: (file: File) => void;
    onInvoiceDelete: () => Promise<unknown> | void;
    onInvoicePreview: () => void;
    onInvoiceDownload: () => void;
    onOpenWireInstructions: () => void;
    onVerifyWireInstructions: (verified: boolean) => void;
    onSeatStateChange: (party: FeeAgreementParty, targetState: ApprovalState) => void;
}

const ACCOUNT_MASK = '•••• •••• ••••';
const VISIBLE_ACCOUNT_DIGITS = 4;

const maskAccountNumber = (accountNumber?: string) => {
    const value = (accountNumber ?? '').replace(/\s/g, '');
    if (!value) {
        return EM_DASH;
    }
    return value.length <= VISIBLE_ACCOUNT_DIGITS
        ? value
        : `${ACCOUNT_MASK} ${value.slice(-VISIBLE_ACCOUNT_DIGITS)}`;
};

const WIRE_SUMMARY_FIELDS: [string, (instructions: WireInstructions) => string][] = [
    ['Beneficiary', (i) => i.beneficiaryName || i.recipientName || EM_DASH],
    ['Bank', (i) => i.bankName || EM_DASH],
    ['Routing (ABA)', (i) => i.routingNumber || EM_DASH],
    ['Account', (i) => maskAccountNumber(i.accountNumber)],
    ['SWIFT', (i) => i.swiftCode || EM_DASH],
    ['Reference', (i) => i.referenceNote || EM_DASH],
];

const AmountEditor = ({
    amount,
    isEditable,
    onCommit,
}: {
    amount: number | null;
    isEditable: boolean;
    onCommit: (nextValue: number | null) => void;
}) => {
    const [value, setValue] = useState(amount != null ? String(amount) : '');
    const isEditingRef = useRef(false);

    useEffect(() => {
        if (!isEditingRef.current) {
            setValue(amount != null ? String(amount) : '');
        }
    }, [amount]);

    if (!isEditable) {
        return <span className="ff-legal__amount-value">{formatMoneyExact(amount)}</span>;
    }

    return (
        <CurrencyInput
            className="form-control ff-legal__amount-input"
            value={value}
            placeholder="$0.00"
            allowDecimals
            decimalsLimit={2}
            allowNegativeValue={false}
            prefix="$"
            groupSeparator=","
            decimalSeparator="."
            onValueChange={(nextValue) => {
                isEditingRef.current = true;
                setValue(nextValue ?? '');
            }}
            onBlur={() => {
                isEditingRef.current = false;
                const nextAmount = value.trim() === '' ? null : Number(value);
                if (nextAmount !== amount) {
                    onCommit(nextAmount);
                }
            }}
        />
    );
};

export const LegalFees = ({
    engagementId,
    legalFees,
    onAmountChange,
    onInvoiceUpload,
    onInvoiceDelete,
    onInvoicePreview,
    onInvoiceDownload,
    onOpenWireInstructions,
    onVerifyWireInstructions,
    onSeatStateChange,
}: LegalFeesProps) => {
    const { invoiceDocument, wireInstructions, wireInstructionsVerification, capabilities } = legalFees;

    // Per-section flag, so the card that changed carries its own badge. Rendering one
    // marks it read, which is what clears the aggregate tab and sub-tab badges too.
    const sectionFlagCode = (sectionCode: string) =>
        `${NotificationFlagsCategories.MpCode}.${engagementId}.${NotificationFlagsCategories.FlowOfFundsCode}.${NotificationFlagsCategories.LegalFeesCode}.${sectionCode}`;
    const invoiceInputRef = useRef<HTMLInputElement>(null);
    const [isConfirmingInvoiceRemoval, setIsConfirmingInvoiceRemoval] = useState(false);
    const [isRemovingInvoice, setIsRemovingInvoice] = useState(false);

    // The dialog stays up, with the confirm button in its loading state, until the
    // removal request comes back.
    const removeInvoice = async () => {
        setIsRemovingInvoice(true);
        try {
            await onInvoiceDelete();
        } finally {
            setIsRemovingInvoice(false);
            setIsConfirmingInvoiceRemoval(false);
        }
    };

    return (
        <div className="ff-legal">
            <div className="main-card card ff-legal__card">
                <div className="ff-legal__card-head">
                    <h4>
                        <span className="ff-legal__flagged">
                            Legal Fees Invoice
                            <NotificationFlag
                                eventCode={sectionFlagCode(
                                    NotificationFlagsCategories.LegalFeesInvoiceCode
                                )}
                                doNotRemove={true}
                            />
                        </span>
                    </h4>
                    {invoiceDocument && (
                        <span className="ff-legal__card-meta">
                            Uploaded {formatDate(invoiceDocument.uploadedOn)}
                        </span>
                    )}
                </div>
                <div className="card-body">
                    {invoiceDocument ? (
                        <div className="ff-legal__file">
                            <span className="ff-legal__file-icon">
                                <FontAwesomeIcon
                                    icon={
                                        invoiceDocument.contentType === PDF_CONTENT_TYPE
                                            ? faFilePdf
                                            : faFileLines
                                    }
                                />
                            </span>
                            <span className="ff-legal__file-info">
                                <span className="ff-legal__file-name">
                                    {invoiceDocument.fileName}
                                </span>
                                <span className="ff-legal__file-meta">
                                    {formatFileSize(invoiceDocument.fileSizeBytes)} ·{' '}
                                    {formatFileType(invoiceDocument.contentType)}
                                    {invoiceDocument.pageCount
                                        ? ` · ${invoiceDocument.pageCount} pages`
                                        : ''}
                                </span>
                            </span>
                            <span className="ff-legal__file-actions">
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary btn-sm"
                                    aria-label="Preview invoice"
                                    onClick={onInvoicePreview}>
                                    <FontAwesomeIcon icon={faEye} />
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary btn-sm"
                                    aria-label="Download invoice"
                                    onClick={onInvoiceDownload}>
                                    <FontAwesomeIcon icon={faDownload} />
                                </button>
                                {capabilities.canEditInvoice && (
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary btn-sm"
                                        aria-label="Remove invoice"
                                        onClick={() => setIsConfirmingInvoiceRemoval(true)}>
                                        <FontAwesomeIcon icon={faXmark} />
                                    </button>
                                )}
                            </span>
                        </div>
                    ) : capabilities.canEditInvoice ? (
                        <>
                            <button
                                type="button"
                                className="ff-legal__upload"
                                onClick={() => invoiceInputRef.current?.click()}>
                                <FontAwesomeIcon icon={faUpload} /> Upload Invoice
                            </button>
                            <input
                                ref={invoiceInputRef}
                                type="file"
                                className="ff-legal__file-input"
                                accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.png,.jpg,.jpeg"
                                onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) {
                                        onInvoiceUpload(file);
                                    }
                                    e.target.value = '';
                                }}
                            />
                        </>
                    ) : (
                        <p className="ff-legal__empty">No invoice uploaded yet.</p>
                    )}
                </div>
            </div>

            {invoiceDocument && (
                <ConfirmationModal
                    isVisible={isConfirmingInvoiceRemoval}
                    setIsVisible={() => setIsConfirmingInvoiceRemoval(false)}
                    header={'Are you sure?'}
                    confirmationText={`Are you sure you want to remove ${invoiceDocument.fileName}?`}
                    onConfirm={removeInvoice}
                    closeOnConfirm={false}
                    isLoading={isRemovingInvoice}
                    nextButtonLoaderText={'Removing...'}
                    isConfirmationDanger={true}
                    cancelButtonText={'No, take me back'}
                    nextButtonText={"Yes, I'm sure"}
                />
            )}

            <div className="main-card card ff-legal__card">
                <div className="card-body ff-legal__amount">
                    <span className="ff-legal__amount-label">Legal Fees Owed at Close</span>
                    <span className="ff-legal__flagged ff-legal__flagged--value">
                        <AmountEditor
                            amount={legalFees.amountOwedAtClose}
                            isEditable={capabilities.canEditAmount}
                            onCommit={onAmountChange}
                        />
                        <NotificationFlag
                            eventCode={sectionFlagCode(NotificationFlagsCategories.LegalFeesAmountCode)}
                            doNotRemove={true}
                        />
                    </span>
                </div>
            </div>

            <div className="main-card card ff-legal__card">
                <div className="ff-legal__card-head">
                    <h4>
                        <span className="ff-legal__flagged">
                            Wire &amp; Payment Instructions
                            <NotificationFlag
                                eventCode={sectionFlagCode(
                                    NotificationFlagsCategories.LegalFeesWireInstructionsCode
                                )}
                                doNotRemove={true}
                            />
                        </span>
                    </h4>
                    <span className="ff-legal__card-meta">
                        {wireInstructionsVerification ? (
                            <span className="ff-legal__verified">
                                <FontAwesomeIcon icon={faCircleCheck} /> Verified by VetValue ·{' '}
                                {wireInstructionsVerification.verifiedByName} ·{' '}
                                {formatDate(wireInstructionsVerification.verifiedOn)}
                            </span>
                        ) : (
                            'Not yet verified'
                        )}
                        {!capabilities.canEditWireInstructions && ' · Read only'}
                    </span>
                </div>
                <div className="card-body ff-legal__wire-body">
                    {wireInstructions ? (
                        <dl className="ff-legal__wire-list">
                            {WIRE_SUMMARY_FIELDS.map(([label, readValue]) => (
                                <div key={label} className="ff-legal__wire-row">
                                    <dt className="ff-legal__wire-label">{label}</dt>
                                    <dd className="ff-legal__wire-value">
                                        {readValue(wireInstructions)}
                                    </dd>
                                </div>
                            ))}
                        </dl>
                    ) : (
                        <p className="ff-legal__empty">
                            Wire instructions have not been provided yet.
                        </p>
                    )}
                    <div className="ff-legal__wire-actions">
                        <button
                            type="button"
                            className="btn btn-outline-primary btn-sm ff-legal__wire-btn"
                            disabled={!wireInstructions && !capabilities.canEditWireInstructions}
                            onClick={onOpenWireInstructions}>
                            <FontAwesomeIcon icon={faCreditCard} />{' '}
                            {capabilities.canEditWireInstructions
                                ? 'View / Edit Wire Instructions'
                                : 'View Wire Instructions'}
                        </button>
                        {capabilities.canVerifyWireInstructions && (
                            <button
                                type="button"
                                className="btn btn-primary btn-sm"
                                onClick={() => onVerifyWireInstructions(!wireInstructionsVerification)}>
                                {wireInstructionsVerification ? 'Unmark Verified' : 'Mark Verified'}
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <FeeAgreementBlock
                agreement={legalFees.agreement}
                seatFlagEventCode={sectionFlagCode(
                    NotificationFlagsCategories.LegalFeesAgreementCode
                )}
                description="Both the Seller’s Counsel and the Seller must agree to these legal fees before they are confirmed."
                onSeatStateChange={onSeatStateChange}
            />
        </div>
    );
};
